/**
 * Abteilungen (CRM 2.0): API-Client + Typen.
 *
 * Alle Endpunkte liegen unter /v2/admin (Antwortformat {data, meta, errors}).
 * Spezifikation: sh24-api docs/abteilungen-spec.md.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiV2, type DashboardConfig } from './api';

export type DepartmentPipeline = 'pb' | 'bk' | 'custom';
/** Personengruppe: 2 = PB (Pflegebedürftige), 1 = BK (Betreuungskräfte). */
export type DepartmentUserRole = 1 | 2;

export interface Department {
  id: number;
  name: string;
  slug: string;
  color: string;
  description: string | null;
  user_role: DepartmentUserRole;
  pipeline: DepartmentPipeline;
  is_active: boolean;
  sort?: number;
  members_count: number;
  stages_count: number;
  widgets_count?: number;
  role_ids: number[];
}

export interface DepartmentMember {
  id: number;
  name: string;
  email: string;
  role?: string | { id: number; name: string } | null;
  /** Slugs aller Abteilungen des Mitglieds. */
  departments: string[];
}

export interface DepartmentStage {
  id: number;
  title: string;
  color: string;
  description?: string | null;
  is_active: boolean;
  /** Bei pb/bk: Position unter den aktiven Phasen (null = inaktiv). */
  position: number | null;
  count?: number;
}

export interface DepartmentDetail extends Department {
  members: DepartmentMember[];
  dashboard_config: DashboardConfig | null;
  stages: DepartmentStage[];
}

export interface DepartmentCreatePayload {
  name: string;
  role_ids?: number[];
  slug: string;
  color?: string;
  description?: string;
  user_role: DepartmentUserRole;
}

export interface DepartmentUpdatePayload {
  name: string;
  color: string;
  description: string | null;
  is_active: boolean;
}

export interface DepartmentStagePayload {
  title: string;
  color: string;
  description?: string | null;
  is_active?: boolean;
}

/** Eintrag der eigenen Abteilungen (Menü). */
export interface MyDepartment {
  id: number;
  name: string;
  slug: string;
  color: string;
  user_role: DepartmentUserRole;
  pipeline: DepartmentPipeline;
  workflow_count: number;
}

export interface DepartmentDashboard {
  department: MyDepartment;
  config: DashboardConfig | null;
}

export interface WorkflowStage {
  id: number;
  title: string;
  color: string;
  position: number;
  count: number;
}

export interface WorkflowItem {
  user_id: number;
  name: string;
  stage: number;
}

export interface DepartmentWorkflow {
  stages: WorkflowStage[];
  items: WorkflowItem[];
}

export interface WorkflowCandidate {
  user_id: number;
  name: string;
}

export interface RbacPermission {
  key: string;
  label: string;
  group: string;
}

export interface RbacRole {
  id: number;
  name: string;
  permissions: string[];
}

const BASE = '/admin/departments';

export const departmentsApi = {
  list: async (): Promise<Department[]> => {
    const res = await apiV2.get<{ data: Department[] }>(BASE);
    return res.data.data ?? [];
  },
  get: async (id: number): Promise<DepartmentDetail> => {
    const res = await apiV2.get<{ data: DepartmentDetail }>(`${BASE}/${id}`);
    return res.data.data;
  },
  create: async (payload: DepartmentCreatePayload): Promise<Department> => {
    const res = await apiV2.post<{ data: Department }>(BASE, payload);
    return res.data.data;
  },
  update: async (id: number, payload: DepartmentUpdatePayload): Promise<Department> => {
    const res = await apiV2.put<{ data: Department }>(`${BASE}/${id}`, payload);
    return res.data.data;
  },
  remove: async (id: number): Promise<void> => {
    await apiV2.delete(`${BASE}/${id}`);
  },
  setMembers: async (id: number, adminIds: number[]): Promise<void> => {
    await apiV2.put(`${BASE}/${id}/members`, { admin_ids: adminIds });
  },
  setRoles: async (id: number, roleIds: number[]): Promise<void> => {
    await apiV2.put(`${BASE}/${id}/roles`, { role_ids: roleIds });
  },
  saveDashboard: async (id: number, config: DashboardConfig): Promise<void> => {
    await apiV2.put(`${BASE}/${id}/dashboard`, { config });
  },

  // Stages (pb/bk: read-only, custom: bearbeitbar)
  stages: async (id: number): Promise<DepartmentStage[]> => {
    const res = await apiV2.get<{ data: DepartmentStage[] }>(`${BASE}/${id}/stages`);
    return res.data.data ?? [];
  },
  createStage: async (id: number, payload: DepartmentStagePayload): Promise<DepartmentStage> => {
    const res = await apiV2.post<{ data: DepartmentStage }>(`${BASE}/${id}/stages`, payload);
    return res.data.data;
  },
  updateStage: async (id: number, stageId: number, payload: DepartmentStagePayload): Promise<DepartmentStage> => {
    const res = await apiV2.put<{ data: DepartmentStage }>(`${BASE}/${id}/stages/${stageId}`, payload);
    return res.data.data;
  },
  /** Löschen; `targetStageId` verschiebt enthaltene Personen (Pflicht, wenn welche drin sind). */
  deleteStage: async (id: number, stageId: number, targetStageId?: number | null): Promise<void> => {
    await apiV2.delete(`${BASE}/${id}/stages/${stageId}`, {
      params: targetStageId ? { target_stage_id: targetStageId } : undefined,
    });
  },
  reorderStages: async (id: number, ids: number[]): Promise<void> => {
    await apiV2.put(`${BASE}/${id}/stages/reorder`, { ids });
  },

  // Persönliche Rollen / Abteilungen je Admin
  adminExtraRoles: async (adminId: number): Promise<number[]> => {
    const res = await apiV2.get<{ data: { role_ids: number[] } }>(`/admin/admins/${adminId}/extra-roles`);
    return res.data.data?.role_ids ?? [];
  },
  setAdminExtraRoles: async (adminId: number, roleIds: number[]): Promise<void> => {
    await apiV2.put(`/admin/admins/${adminId}/extra-roles`, { role_ids: roleIds });
  },
  adminDepartments: async (adminId: number): Promise<number[]> => {
    const res = await apiV2.get<{ data: { department_ids: number[] } }>(`/admin/admins/${adminId}/departments`);
    return res.data.data?.department_ids ?? [];
  },
  setAdminDepartments: async (adminId: number, departmentIds: number[]): Promise<void> => {
    await apiV2.put(`/admin/admins/${adminId}/departments`, { department_ids: departmentIds });
  },

  // Mitglieder-Sicht
  mine: async (): Promise<MyDepartment[]> => {
    const res = await apiV2.get<{ data: MyDepartment[] }>('/admin/me/departments');
    return res.data.data ?? [];
  },
  dashboardBySlug: async (slug: string): Promise<DepartmentDashboard> => {
    const res = await apiV2.get<{ data: DepartmentDashboard }>(`${BASE}/by-slug/${encodeURIComponent(slug)}/dashboard`);
    return res.data.data;
  },
  workflowBySlug: async (slug: string): Promise<DepartmentWorkflow> => {
    const res = await apiV2.get<{ data: DepartmentWorkflow }>(`${BASE}/by-slug/${encodeURIComponent(slug)}/workflow`);
    return res.data.data;
  },
  moveInWorkflow: async (slug: string, userId: number, stage: number): Promise<void> => {
    await apiV2.put(`${BASE}/by-slug/${encodeURIComponent(slug)}/workflow/${userId}`, { stage });
  },

  /** Personen, die noch nicht in der Pipeline stehen (q ab 2 Zeichen, bis 20). */
  workflowCandidates: async (slug: string, q: string): Promise<WorkflowCandidate[]> => {
    const res = await apiV2.get<{ data: WorkflowCandidate[] }>(
      `${BASE}/by-slug/${encodeURIComponent(slug)}/workflow/candidates`, { params: { q } },
    );
    return res.data.data ?? [];
  },

  /** Rechte mit Anzeigename (wie Rollen & Rechte). */
  permissions: async (): Promise<RbacPermission[]> => {
    const res = await apiV2.get<{ data: RbacPermission[] }>('/admin/rbac/permissions');
    return res.data.data ?? [];
  },

  /** Vorhandene Rollen (Rollen & Rechte), nur lesend: Zuweisung, kein Anlegen. */
  roles: async (): Promise<RbacRole[]> => {
    const res = await apiV2.get<{ data: RbacRole[] }>('/admin/rbac/roles');
    return res.data.data ?? [];
  },
};

// ── Eigene Abteilungen (Menü) ────────────────────────────────────────────────

/** Gemeinsamer Cache, damit Sidebar und App-Kopf nur einmal laden. */
let mineInflight: Promise<MyDepartment[]> | null = null;
let mineCache: MyDepartment[] | null = null;
const mineListeners = new Set<(d: MyDepartment[]) => void>();

export function loadMyDepartments(force = false): Promise<MyDepartment[]> {
  if (!force && mineInflight) return mineInflight;
  const p = departmentsApi.mine()
    .then(d => {
      mineCache = d;
      mineListeners.forEach(l => l(d));
      return d;
    })
    .catch(() => mineCache ?? [])
    .finally(() => { if (mineInflight === p) mineInflight = null; });
  mineInflight = p;
  return p;
}

/** Nur für Tests: Cache leeren. */
export function resetMyDepartmentsCache(): void {
  mineCache = null;
  mineInflight = null;
}

/**
 * Eigene Abteilungen; lädt beim Einbinden und bei `refresh()` neu.
 * `enabled = false` (nicht angemeldet) lädt NICHT: ein 401 würde sonst den
 * globalen Redirect auf /login auslösen (Reload-Schleife, MFA-Abbruch).
 */
export function useMyDepartments(enabled = true): { departments: MyDepartment[]; loading: boolean; refresh: () => void } {
  const [departments, setDepartments] = useState<MyDepartment[]>(enabled ? (mineCache ?? []) : []);
  const [loading, setLoading] = useState(enabled && mineCache == null);

  useEffect(() => {
    if (!enabled) { setDepartments([]); return; }
    let alive = true;
    const listener = (d: MyDepartment[]) => { if (alive) setDepartments(d); };
    mineListeners.add(listener);
    void loadMyDepartments().then(d => {
      if (!alive) return;
      setDepartments(d);
      setLoading(false);
    });
    return () => { alive = false; mineListeners.delete(listener); };
  }, [enabled]);

  const refresh = useCallback(() => { if (enabled) void loadMyDepartments(true); }, [enabled]);
  return { departments, loading, refresh };
}
